import { ChevronDownIcon, CrossIcon } from '../../../../icons';
import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useMemo, forwardRef, ForwardedRef } from 'react';
import cx from 'classnames';
import styles from './DropdownButton.module.css';
import { useTranslation } from 'react-i18next';
import Text from '@/storybook/components/Text/Text';
import { PropsWithChildren } from 'react';

export enum DropdownButtonType {
  'singleSelect' = 'singleSelect',
  'multiSelect' = 'multiSelect',
  'treeSelect' = 'treeSelect',
  'multiLevelSelect' = 'multiLevelSelect',
  'singleLevelSelect' = 'singleLevelSelect',
}

export enum DropdownButtonSize {
  L = 'L',
  M = 'M',
  S = 'S',
}

export const CES_DROPDOWN_TRIGGER_SELECTOR = '[data-ces-dropdown-trigger]';

export type DropdownButtonProps = {
  'placeholder'?: ReactNode;
  'numOfValue': number;
  'type': keyof typeof DropdownButtonType;
  'size'?: keyof typeof DropdownButtonSize;
  'onClick'?: VoidFunction;
  'onClear'?: VoidFunction;
  'clearable'?: boolean;
  'disabled'?: boolean;
  'active': boolean;
  'data-testid'?: string;
  'forceTruncate'?: boolean;
  'className'?: string;
  'dropdownIndicator'?: ReactNode;
  /**
   * Cascading filters: overrides the clear-count label with `{active}/{total}`
   * when some selected values are disabled. Falls back to `numOfValue`.
   */
  'countLabel'?: string;
};

function DropdownButton(
  {
    placeholder,
    numOfValue,
    onClick,
    onClear,
    clearable = false,
    disabled,
    active,
    'data-testid': dataTestId = 'dropdownButton',
    type = 'singleSelect',
    size = 'L',
    className,
    dropdownIndicator,
    children,
    forceTruncate,
    countLabel,
  }: PropsWithChildren<DropdownButtonProps>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { t } = useTranslation();

  const handleOnClear = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      onClear?.();
    },
    [onClear],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.();
      }
    },
    [disabled, onClick],
  );

  const isClearable = useMemo(
    () => !disabled && clearable && onClear && numOfValue > 0,
    [clearable, disabled, numOfValue, onClear],
  );

  const isDisplayClearWithValueCount = useMemo(() => {
    return (
      (type === DropdownButtonType.multiLevelSelect ||
        type === DropdownButtonType.multiSelect ||
        type === DropdownButtonType.treeSelect) &&
      isClearable
    );
  }, [type, isClearable]);

  const isDisplayClearWithoutValueCount = useMemo(() => {
    return (
      (type === DropdownButtonType.singleSelect && isClearable) ||
      (type === DropdownButtonType.singleLevelSelect && isClearable)
    );
  }, [type, isClearable]);

  return (
    <div
      className={cx(styles.dropdownButtonContainer, className)}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-ces-dropdown-trigger=""
      data-is-active={active}
      data-testid={dataTestId}
      data-is-disabled={disabled}
      data-is-clearable={isClearable}
      data-is-force-truncate={forceTruncate}
      data-has-value={numOfValue > 0}
      data-size={size}
      role="button"
      tabIndex={disabled ? -1 : 0}
      ref={ref}
    >
      {numOfValue > 0 && (
        <div className={styles.valueList} data-testid={`value-${type}-list`}>
          {children}
        </div>
      )}
      {!(numOfValue > 0) && (
        <span className={styles.placeholder} data-testid="dropdownPlaceholder">
          {placeholder}
        </span>
      )}
      <div className={styles.additionalCta} data-testid="dropdownCTA">
        {isDisplayClearWithValueCount && (
          <span
            className={styles.countContainer}
            onClick={handleOnClear}
            data-wording="clear"
            data-testid="dropdownValueCount"
          >
            <Text className={styles.count}>{countLabel ?? numOfValue}</Text>
            <Text className={styles.wording}>{t('global.filters.clear')}</Text>
          </span>
        )}
        {isDisplayClearWithoutValueCount && <CrossIcon onClick={handleOnClear} data-testid="dropdownClearButton" />}
        {!disabled && (dropdownIndicator ?? <ChevronDownIcon className={styles.chevronIcon} />)}
      </div>
    </div>
  );
}

export default forwardRef(DropdownButton);
